"use client";
import useTranslate from "@app/_hooks/useTranslate";
import debounce from "lodash.debounce";
import { useSearchParams } from "next/navigation";
import type { ChangeEvent, ComponentProps } from "react";
import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { useSearchStore } from "@/stores/searchStore";

import SearchIcon from "@app/_icons/SearchIcon/SearchIcon";
import { useSearchContext } from "./Search.hooks";
import SearchSubmit from "./SearchSubmit";

type SearchInputProps = ComponentProps<"input"> & {
  className?: string;
};

const SearchInput = ({ className, ...props }: SearchInputProps) => {
  const t = useTranslate();
  const { inputRef } = useSearchContext();
  const params = useSearchParams();
  const [searchValue, setSearchValue] = useState(params.get("query") || "");
  const setSearchQuery = useSearchStore((state) => state.setSearchQuery);

  const debouncedSetSearchQuery = useMemo(
    () => debounce(setSearchQuery, 300),
    [setSearchQuery],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSetSearchQuery(event.target.value);
  };

  return (
    <label className="relative grid">
      <input
        ref={inputRef}
        className={cn(
          "col-span-full row-span-full min-w-60 rounded-none border-b pl-10! placeholder:text-sm",
          className,
        )}
        name="query"
        placeholder={t("searchTerm")}
        data-testid="search-input"
        value={searchValue}
        onChange={handleChange}
        {...props}
      />
      <SearchSubmit className="pointer-events-none z-10 col-span-full row-span-full ml-3 self-center justify-self-start lg:ml-2">
        <SearchIcon className="size-4" />
        <span className="sr-only">{t("submit")}</span>
      </SearchSubmit>
    </label>
  );
};

SearchInput.displayName = "SearchInput";

export default SearchInput;
